import type { CSSProperties } from 'react';

/** Grüner/weißer Check, durchgehend in Status-Cells, Trade-Rows, Scanner verwendet. */
export function Check({
  size = 17,
  stroke = '#fff',
  width = 4,
  style,
}: {
  size?: number;
  stroke?: string;
  width?: number;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

/** Doppelklack Brand-Mark: zwei Sticker-Kacheln, die zum „Klack" zusammenklacken.
 *  Weiße Karte mit grünem Check (habe) + goldenes ×2 (doppelt) auf Marken-Gradient.
 *  Skaliert bis 34px runter — withBadge/badgeText/withGhost steuern Stufen. */
export function AppIcon({
  size = 78,
  withBadge = true,
  badgeText = true,
  withGhost = true,
  checkWidth = 6.5,
  gradId = 'dkbg',
  dropShadow = false,
}: {
  size?: number;
  withBadge?: boolean;
  badgeText?: boolean;
  withGhost?: boolean;
  checkWidth?: number;
  gradId?: string;
  dropShadow?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 92 92"
      style={{
        flex: 'none',
        filter: dropShadow ? 'drop-shadow(0 10px 22px rgba(47,111,237,.4))' : undefined,
      }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3B79FF" />
          <stop offset="1" stopColor="#7B5BFF" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="88" height="88" rx="22" fill={`url(#${gradId})`} />
      {withGhost && (
        <rect
          x="22"
          y="27"
          width="38"
          height="38"
          rx="9"
          fill="#0B0F15"
          opacity=".26"
          transform="rotate(-9 41 46)"
        />
      )}
      <rect x="30" y="31" width="40" height="40" rx="11" fill="#fff" />
      <path
        d="M40 51l6 6 13-14"
        fill="none"
        stroke="#1FA85C"
        strokeWidth={checkWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {withBadge && <circle cx="65" cy="30" r="11.5" fill="#F2A91C" stroke="#fff" strokeWidth="3" />}
      {withBadge && badgeText && (
        <text
          x="65"
          y="34.5"
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize="12"
          fontWeight="700"
          fill="#1A1206"
        >
          2
        </text>
      )}
    </svg>
  );
}

/** Avatar mit Initialen, optional Team-Akzent über --color-primary. */
export function Avatar({
  name,
  size = 44,
  team = false,
}: {
  name: string;
  size?: number;
  team?: boolean;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.32),
        background: team ? 'var(--color-primary)' : 'var(--color-surface-2)',
        color: team ? '#fff' : 'var(--color-text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Hanken Grotesk', sans-serif",
        fontWeight: 800,
        fontSize: Math.round(size * 0.34),
        flex: 'none',
      }}
    >
      {initials}
    </span>
  );
}
